/**
 * Interview Assessment Selectors
 * Memoized selectors for interview assessment state
 */

import { RootState } from "../index";
import { createSelector } from "@reduxjs/toolkit";

// ============================================================================
// Base Selectors
// ============================================================================

/**
 * Select interview assessment state
 */
export const selectInterviewAssessment = (state: RootState) =>
  state.interviewAssessment;

// ============================================================================
// Memoized Selectors
// ============================================================================

/**
 * Select assessment ID
 */
export const selectAssessmentId = createSelector(
  selectInterviewAssessment,
  (state) => state.assessmentId
);

/**
 * Select assessment status
 */
export const selectAssessmentStatus = createSelector(
  selectInterviewAssessment,
  (state) => state.status
);

/**
 * Select if currently submitting assessment
 */
export const selectIsSubmitting = createSelector(
  selectInterviewAssessment,
  (state) => state.isSubmitting
);

/**
 * Select submission error
 */
export const selectSubmissionError = createSelector(
  selectInterviewAssessment,
  (state) => state.submissionError
);

/**
 * Select if currently polling
 */
export const selectIsPolling = createSelector(
  selectInterviewAssessment,
  (state) => state.isPolling
);

/**
 * Select poll attempts count
 */
export const selectPollAttempts = createSelector(
  selectInterviewAssessment,
  (state) => state.pollAttempts
);

/**
 * Select progress message
 */
export const selectProgressMessage = createSelector(
  selectInterviewAssessment,
  (state) => state.progressMessage
);

/**
 * Select polling error
 */
export const selectPollingError = createSelector(
  selectInterviewAssessment,
  (state) => state.pollingError
);

/**
 * Select assessment feedback
 */
export const selectFeedback = createSelector(
  selectInterviewAssessment,
  (state) => state.feedback
);

/**
 * Select if fetching feedback
 */
export const selectIsFetchingFeedback = createSelector(
  selectInterviewAssessment,
  (state) => state.isFetchingFeedback
);

/**
 * Select fetch feedback error
 */
export const selectFetchFeedbackError = createSelector(
  selectInterviewAssessment,
  (state) => state.fetchFeedbackError
);

// ============================================================================
// Computed Selectors
// ============================================================================

/**
 * Select if assessment is completed
 */
export const selectIsAssessmentCompleted = createSelector(
  selectAssessmentStatus,
  (status) => status === "completed"
);

/**
 * Select if assessment failed
 */
export const selectIsAssessmentFailed = createSelector(
  selectAssessmentStatus,
  (status) => status === "error"
);

/**
 * Select if assessment is still processing
 */
export const selectIsAssessmentProcessing = createSelector(
  selectAssessmentStatus,
  (status) => status === "processing"
);

/**
 * Select feedback blocks sorted by position
 */
export const selectFeedbackBlocks = createSelector(
  selectFeedback,
  (feedback) => {
    if (!feedback?.interviewPracticeFeedback?.blocks) {
      return [];
    }
    return [...feedback.interviewPracticeFeedback.blocks].sort(
      (a, b) => a.position - b.position
    );
  }
);

/**
 * Select if any operation is in progress
 */
export const selectIsLoading = createSelector(
  [selectIsSubmitting, selectIsPolling, selectIsFetchingFeedback],
  (isSubmitting, isPolling, isFetchingFeedback) =>
    isSubmitting || isPolling || isFetchingFeedback
);

/**
 * Select if there are any errors
 */
export const selectHasErrors = createSelector(
  [selectSubmissionError, selectPollingError, selectFetchFeedbackError],
  (submissionError, pollingError, fetchError) =>
    !!submissionError || !!pollingError || !!fetchError
);

/**
 * Select combined error message
 */
export const selectErrorMessage = createSelector(
  [selectSubmissionError, selectPollingError, selectFetchFeedbackError],
  (submissionError, pollingError, fetchError) =>
    submissionError || pollingError || fetchError || null
);

/**
 * Select current progress step
 */
export const selectCurrentStep = createSelector(
  selectInterviewAssessment,
  (state) => state.currentStep
);

/**
 * Select completed steps array
 */
export const selectCompletedSteps = createSelector(
  selectInterviewAssessment,
  (state) => state.completedSteps
);
